import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import SignatureCanvas from './SignatureCanvas';

const NON_CONTROLLED_MEDICATIONS = [
  { name: 'Acetaminophen IV', concentration: '1000mg/100ml' },
  { name: 'Adenosine', concentration: '6mg/2ml' },
  { name: 'Albuterol', concentration: '2.5mg/3.0ml' },
  { name: 'Amiodarone', concentration: '150mg/3ml' },
  { name: 'Aspirin', concentration: '81mg/1 Tab' },
  { name: 'Atropine', concentration: '1mg/10ml' },
  { name: 'Calcium Chloride', concentration: '1000mg/10ml' },
  { name: 'Cardene', concentration: '40mg/200ml' },
  { name: 'Dexamethasone', concentration: '20mg/5ml' },
  { name: 'Dextrose 10% IV', concentration: '25g/250mL' },
  { name: 'Dextrose 25%', concentration: '' },
  { name: 'Dextrose 50%', concentration: '25g/50ml' },
  { name: 'Diltiazem', concentration: '25mg/5ml' },
  { name: 'Diphenhydramine', concentration: '50mg/1ml' },
  { name: 'Dobutamine', concentration: '250mg/20ml' },
  { name: 'Droperidol', concentration: '5mg/2ml' },
  { name: 'Duoneb', concentration: '0.5/3ml' },
  { name: 'Epi (1:1000)', concentration: '1mg/1ml' },
  { name: 'Epi (1:10000)', concentration: '0.1mg/1ml' },
  { name: 'Furosemide', concentration: '40mg/4ml' },
  { name: 'Glucagon', concentration: '1 unit' },
  { name: 'Glutose', concentration: '15g' },
  { name: 'Ketorolac', concentration: '30mg/1ml' },
  { name: 'Labetalol', concentration: '5mg/1ml' },
  { name: 'Levetiracetam', concentration: '500mg/5ml' },
  { name: 'Lidocaine', concentration: '100mg/5ml' },
  { name: 'Mag Sulfate', concentration: '1g/2ml' },
  { name: 'Mannitol', concentration: '50g/250ml' },
  { name: 'Narcan', concentration: '2mg/2ml' },
  { name: 'Nitro Infusion', concentration: '200mcg/ml' },
  { name: 'Nitro Tabs', concentration: '0.04mg/1 Tab' },
  { name: 'Norepinephrine', concentration: '4mg/4ml' },
  { name: 'Ondansetron IV', concentration: '4mg/2ml' },
  { name: 'Ondansetron ODT', concentration: '4mg/oral tab' },
  { name: 'Oxytocin/Pitocin', concentration: '10U/1ml' },
  { name: 'Phenylephrine', concentration: '10mg/1ml' },
  { name: 'Potassium', concentration: '10mEq/50ml' },
  { name: 'Racemic Epi', concentration: '13.5mg/0.5ml' },
  { name: 'Rocuronium', concentration: '100mg/10mL' },
  { name: 'Sodium Bicarb', concentration: '50mEq/50ml' },
  { name: 'Sterile Water', concentration: '10ml' },
  { name: 'TXA', concentration: '1000mg/10ml' },
  { name: 'Vasopressin', concentration: '20U/1ml' }
];

function formatMedDisplay(med) {
  if (med.concentration) {
    return `${med.name} (${med.concentration})`;
  }
  return med.name;
}

const CREW_ACTIONS = [
  'Patient Use Restock',
  'Truck Restock'
];

const ADMIN_ACTIONS = [
  'Inventory/Audit',
  'Truck Restock',
  'Patient Use Restock',
  'Removing Expired Medication',
  'Cabinet Inventory Restock'
];

function NonControlledModal({ userInfo, isAdmin = false, onClose, onSubmit }) {
  const [action, setAction] = useState('');
  const [runNumber, setRunNumber] = useState('');
  const [truckNumber, setTruckNumber] = useState('');
  const [medicationItems, setMedicationItems] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [primarySignature, setPrimarySignature] = useState(null);
  const [showPrimaryCanvas, setShowPrimaryCanvas] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');

  const availableActions = isAdmin ? ADMIN_ACTIONS : CREW_ACTIONS;

  const handleActionChange = (value) => {
    setAction(value);
    setRunNumber('');
    setTruckNumber('');
    setMedicationItems([]);
  };

  const addMedicationItem = () => {
    const newItem = { medication: '', amount: '' };
    if (action === 'Removing Expired Medication') {
      newItem.expiration_date = '';
    }
    setMedicationItems([...medicationItems, newItem]);
  };

  const removeMedicationItem = (index) => {
    setMedicationItems(medicationItems.filter((_, i) => i !== index));
  };

  const updateMedicationItem = (index, field, value) => {
    const updated = [...medicationItems];
    updated[index][field] = value;
    setMedicationItems(updated);
  };

  const showAlertPopup = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const uploadSignature = async (base64Data, folder, fileName) => {
    const base64Response = await fetch(base64Data);
    const blob = await base64Response.blob();

    const { data, error } = await supabase.storage
      .from('inventory')
      .upload(`${folder}/signatures/${fileName}`, blob, {
        contentType: 'image/png'
      });

    if (error) throw error;

    // Stored as a storage path; displayed through a short-lived signed URL.
    return data.path;
  };

  const actionRequiresMeds = (act) => {
    return [
      'Patient Use Restock',
      'Truck Restock',
      'Removing Expired Medication',
      'Cabinet Inventory Restock'
    ].includes(act);
  };

  const handleSubmit = async () => {
    if (!action) {
      showAlertPopup('Please select an action.');
      return;
    }

    if (action === 'Patient Use Restock' && !runNumber) {
      showAlertPopup('Run Number is required for Patient Use Restock.');
      return;
    }

    if (action === 'Truck Restock' && !truckNumber) {
      showAlertPopup('Truck Number is required for Truck Restock.');
      return;
    }

    if (actionRequiresMeds(action)) {
      if (medicationItems.length === 0) {
        showAlertPopup('Please add at least one medication.');
        return;
      }
      const hasEmpty = medicationItems.some(item => !item.medication || !item.amount);
      if (hasEmpty) {
        showAlertPopup('All medication items must have a medication selected and an amount entered.');
        return;
      }
      if (action === 'Removing Expired Medication') {
        const missingExp = medicationItems.some(item => !item.expiration_date);
        if (missingExp) {
          showAlertPopup('All items must have an expiration date for Removing Expired Medication.');
          return;
        }
      }
    }

    if (!primarySignature) {
      showAlertPopup('Signature is required.');
      return;
    }

    setLoading(true);

    try {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const sigFileName = `${timestamp}_${randomId}_primary.png`;
      const primarySignatureUrl = await uploadSignature(primarySignature, 'non_controlled', sigFileName);

      const enrichedMedItems = medicationItems.map(item => {
        const medInfo = NON_CONTROLLED_MEDICATIONS.find(m => m.name === item.medication);
        return {
          medication: item.medication,
          concentration: medInfo?.concentration || '',
          amount: item.amount,
          ...(item.expiration_date ? { expiration_date: item.expiration_date } : {})
        };
      });

      const submissionData = {
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        email: userInfo.email,
        action: action,
        run_number: action === 'Patient Use Restock' ? runNumber : null,
        truck_number: action === 'Truck Restock' ? truckNumber : null,
        medication_items_json: enrichedMedItems.length > 0 ? JSON.stringify(enrichedMedItems) : null,
        primary_signature_url: primarySignatureUrl,
        location: 'EMS Station - Non-Controlled',
        additional_notes: additionalNotes || null,
        created_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('non_controlled_medications')
        .insert([submissionData]);

      if (error) throw error;

      showAlertPopup('Submission logged successfully!', 'success');
      setTimeout(() => {
        onSubmit();
      }, 1500);
    } catch (err) {
      console.error('Error submitting:', err);
      showAlertPopup(`Error: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.7)',
      overflowY: 'auto',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '40px',
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{ color: '#333', fontSize: '24px', margin: 0 }}>
            Non-Controlled Medications
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Logged-in User Display */}
        <div style={{
          background: '#e3f2fd',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '25px'
        }}>
          <p style={{ fontSize: '14px', color: '#1565c0', margin: 0 }}>
            <strong>Submitting as:</strong> {userInfo.firstName} {userInfo.lastName} ({userInfo.email})
          </p>
        </div>

        {/* Action Selection */}
        <div style={{ marginBottom: '25px' }}>
          <label className="form-label">
            Action <span style={{ color: 'red' }}>*</span>
          </label>
          <select
            className="form-input"
            value={action}
            onChange={(e) => handleActionChange(e.target.value)}
          >
            <option value="">Select an action</option>
            {availableActions.map(act => (
              <option key={act} value={act}>{act}</option>
            ))}
          </select>
        </div>

        {/* Conditional: Run Number */}
        {action === 'Patient Use Restock' && (
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">
              Run Number <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={runNumber}
              onChange={(e) => setRunNumber(e.target.value)}
              placeholder="Enter run number"
            />
          </div>
        )}

        {/* Conditional: Truck Number */}
        {action === 'Truck Restock' && (
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">
              Truck Number <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-input"
              value={truckNumber}
              onChange={(e) => setTruckNumber(e.target.value)}
            >
              <option value="">Select Truck</option>
<option value="Mayo 21">Mayo 21</option>
<option value="Mayo 22">Mayo 22</option>
<option value="Mayo 23">Mayo 23</option>
<option value="Mayo 24">Mayo 24</option>
<option value="Mayo 25">Mayo 25</option>
            </select>
          </div>
        )}

        {/* Medication Items */}
        {action && actionRequiresMeds(action) && (
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Medications <span style={{ color: 'red' }}>*</span>
            </label>

            {medicationItems.map((item, index) => (
              <div key={index} style={{
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: action === 'Removing Expired Medication' ? '2fr 1fr 1fr auto' : '2fr 1fr auto',
                  gap: '10px',
                  alignItems: 'center'
                }}>
                  <select
                    className="form-input"
                    value={item.medication}
                    onChange={(e) => updateMedicationItem(index, 'medication', e.target.value)}
                    style={{ margin: 0 }}
                  >
                    <option value="">Select Medication</option>
                    {NON_CONTROLLED_MEDICATIONS.map(med => (
                      <option key={med.name} value={med.name}>
                        {formatMedDisplay(med)}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    className="form-input"
                    value={item.amount}
                    onChange={(e) => updateMedicationItem(index, 'amount', e.target.value)}
                    placeholder="Amount"
                    min="0"
                    style={{ margin: 0 }}
                  />

                  {action === 'Removing Expired Medication' && (
                    <input
                      type="date"
                      className="form-input"
                      value={item.expiration_date || ''}
                      onChange={(e) => updateMedicationItem(index, 'expiration_date', e.target.value)}
                      style={{ margin: 0 }}
                    />
                  )}

                  <button
                    className="btn btn-secondary"
                    onClick={() => removeMedicationItem(index)}
                    style={{
                      padding: '8px 15px',
                      fontSize: '14px',
                      background: '#dc3545',
                      borderColor: '#dc3545',
                      color: 'white',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <button
              className="btn btn-primary"
              onClick={addMedicationItem}
              style={{ marginTop: '10px' }}
            >
              + Add Medication
            </button>
          </div>
        )}

        {/* Inventory/Audit Info */}
        {action === 'Inventory/Audit' && (
          <div style={{
            background: '#fff3e0',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '25px'
          }}>
            <p style={{ fontSize: '14px', color: '#e65100', margin: 0 }}>
              This will log that a periodic inventory check/audit was performed. Add any relevant notes below.
            </p>
          </div>
        )}

        {/* Additional Notes */}
        <div style={{ marginBottom: '25px' }}>
          <h3 className="section-title">Additional Notes</h3>
          <textarea
            className="form-input"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            rows="4"
            placeholder="Enter any additional notes..."
          />
        </div>

        {/* Primary Signature */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">
            Signature <span style={{ color: 'red' }}>*</span>
          </h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            {userInfo.firstName} {userInfo.lastName} ({userInfo.email})
          </p>
          {primarySignature ? (
            <div>
              <img src={primarySignature} alt="Signature" style={{ width: '200px', border: '1px solid #ddd', borderRadius: '8px' }} />
              <button
                className="btn btn-secondary"
                onClick={() => setShowPrimaryCanvas(true)}
                style={{ marginTop: '10px', display: 'block' }}
              >
                Re-sign
              </button>
            </div>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setShowPrimaryCanvas(true)}
            >
              Add Signature
            </button>
          )}
        </div>

        {/* Submit Button */}
        <button
          className="btn btn-primary btn-full"
          onClick={handleSubmit}
          disabled={loading}
          style={{ padding: '15px', fontSize: '18px' }}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Signature Canvas */}
      {showPrimaryCanvas && (
        <SignatureCanvas
          title="Signature"
          onConfirm={(sig) => {
            setPrimarySignature(sig);
            setShowPrimaryCanvas(false);
          }}
          onCancel={() => setShowPrimaryCanvas(false)}
        />
      )}

      {/* Loading Spinner */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          flexDirection: 'column'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            color: 'white',
            marginTop: '20px',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Submitting...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Alert Popup */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              backgroundColor: alertType === 'success' ? '#e8f5e8' : '#fee',
              color: alertType === 'success' ? '#2e7d32' : '#c33'
            }}>
              {alertType === 'success' ? '✓' : '✕'}
            </div>
            <h3 style={{
              margin: '0 0 15px',
              fontSize: '20px',
              color: '#333'
            }}>
              {alertType === 'success' ? 'Success' : 'Error'}
            </h3>
            <p style={{
              margin: '0 0 25px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {alertMessage}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NonControlledModal;
